import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  X, 
  Share2,
  Copy,
  Mail,
  MessageSquare,
  Facebook,
  Twitter,
  Link,
  QrCode,
  Download,
  Tag,
  Check,
  TrendingUp
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { DiscountCode } from '../../services/discountService';
import { formatCurrency, formatDate } from '../../lib/utils';

interface ShareDiscountCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  discount: DiscountCode | null;
}

export function ShareDiscountCodeModal({ isOpen, onClose, discount }: ShareDiscountCodeModalProps) {
  const [copied, setCopied] = useState<string | null>(null);
  const [customMessage, setCustomMessage] = useState('');

  if (!discount) return null;

  const handleClose = () => {
    onClose();
    setCopied(null);
    setCustomMessage('');
  };

  const getShareUrl = () => {
    const baseUrl = import.meta.env.VITE_APP_NODE_ENV === 'production' 
      ? 'https://jampackd.store' 
      : 'http://localhost:3000';
    return `${baseUrl}?discount=${discount.code}`;
  };

  const getDefaultMessage = () => {
    const discountText = discount.type === 'percentage' 
      ? `${discount.value}% off`
      : discount.type === 'fixed'
      ? `$${discount.value} off`
      : 'Free shipping';
    
    return `🎉 Special offer! Get ${discountText} with code ${discount.code} at JamPack'd! ${discount.minOrderAmount > 0 ? `Minimum order $${discount.minOrderAmount}. ` : ''}Valid until ${formatDate(discount.endDate)}. Shop now: https://jampackd.store`;
  };

  const handleCopy = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      toast.success(`${type} copied to clipboard!`);
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      toast.error(`Failed to copy ${type.toLowerCase()}`);
    }
  };

  const handleEmailShare = () => {
    const subject = `Special Discount Code: ${discount.code}`;
    const body = customMessage || getDefaultMessage();
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl, '_blank');
  };

  const handleSMSShare = () => {
    const message = customMessage || getDefaultMessage();
    const smsUrl = `sms:?body=${encodeURIComponent(message)}`;
    window.open(smsUrl, '_blank');
  };

  const handleSocialShare = (platform: 'facebook' | 'twitter') => {
    const message = customMessage || getDefaultMessage();
    const url = getShareUrl();
    
    let shareUrl = '';
    if (platform === 'facebook') {
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(message)}`;
    } else if (platform === 'twitter') {
      shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(url)}`;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return { color: 'bg-green-100 text-green-800', label: 'Active' };
      case 'inactive':
        return { color: 'bg-gray-100 text-gray-800', label: 'Inactive' };
      case 'expired':
        return { color: 'bg-red-100 text-red-800', label: 'Expired' };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: status };
    }
  };

  const statusConfig = getStatusConfig(discount.status);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10001] flex items-center justify-center p-4"
          style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-r from-jamaica-green to-jamaica-green/80 px-8 py-6">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-20"></div>
              
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <Share2 className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      Share Discount Code
                    </motion.h2>
                    <motion.p 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-white/80 text-sm"
                    >
                      Share this discount code with customers
                    </motion.p>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClose}
                    className="text-white hover:bg-white/20 h-8 w-8 p-0 rounded-lg"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)] bg-gradient-to-b from-gray-50/50 to-white">
              <div className="space-y-8">
                {/* Discount Code Preview Card */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-green/5 px-6 py-4 border-b border-jamaica-green/20">
                    <div className="flex items-center gap-2">
                      <Tag className="h-5 w-5 text-jamaica-green" />
                      <h3 className="text-lg font-semibold text-gray-900">Discount Code Preview</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <code className="font-mono font-bold text-2xl text-jamaica-green bg-jamaica-green/10 px-4 py-2 rounded-xl border border-jamaica-green/20">
                            {discount.code}
                          </code>
                          <Badge className={`${statusConfig.color} px-3 py-1 text-sm font-medium`}>
                            {statusConfig.label}
                          </Badge>
                        </div>
                        <p className="text-gray-700 text-lg leading-relaxed">{discount.description}</p>
                        <div className="text-sm text-gray-600 space-x-4">
                          <span>Valid until {formatDate(discount.endDate)}</span>
                          {discount.minOrderAmount > 0 && (
                            <span>Min order: {formatCurrency(discount.minOrderAmount)}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-jamaica-green">
                          {discount.type === 'percentage' ? (
                            `${discount.value}%`
                          ) : discount.type === 'fixed' ? (
                            formatCurrency(discount.value)
                          ) : (
                            'Free Shipping'
                          )}
                        </div>
                        <div className="text-sm text-gray-500 font-medium">
                          {discount.type === 'percentage' ? 'OFF' : discount.type === 'fixed' ? 'OFF' : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Quick Copy Options Card */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-blue-100">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                        <Copy className="h-4 w-4 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Quick Copy</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex justify-center">
                      <div className="space-y-3 w-full max-w-md">
                        <Label className="text-base font-semibold text-gray-900">Discount Code</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            value={discount.code}
                            readOnly
                            className="font-mono h-12 rounded-xl border-gray-200 bg-gray-50 text-base"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopy(discount.code, 'Code')}
                            className="flex-shrink-0 h-12 px-4 rounded-xl hover:bg-jamaica-green/10 hover:border-jamaica-green/30 transition-all duration-200"
                          >
                            {copied === 'Code' ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Custom Message Card */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-purple-100">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                        <MessageSquare className="h-4 w-4 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Customize Message</h3>
                    </div>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="space-y-3">
                      <Label className="text-base font-semibold text-gray-900">
                        Share Message (optional)
                      </Label>
                      <Textarea
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        placeholder={getDefaultMessage()}
                        rows={4}
                        className="resize-none rounded-xl border-gray-200 text-base focus:border-jamaica-green focus:ring-jamaica-green/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                      />
                      <p className="text-xs text-gray-500">
                        Leave empty to use the default message
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopy(customMessage || getDefaultMessage(), 'Message')}
                      className="w-full h-12 rounded-xl hover:bg-jamaica-green/10 hover:border-jamaica-green/30 transition-all duration-200"
                    >
                      {copied === 'Message' ? (
                        <>
                          <Check className="w-4 h-4 mr-2 text-green-600" />
                          Message Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Message
                        </>
                      )}
                    </Button>
                  </div>
                </motion.div>

                {/* Share Options Card */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-emerald-50 to-green-50 px-6 py-4 border-b border-emerald-100">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 bg-gradient-to-r from-emerald-500 to-green-600 rounded-lg flex items-center justify-center">
                        <Share2 className="h-4 w-4 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Share Options</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Button
                        variant="outline"
                        onClick={handleEmailShare}
                        className="flex flex-col items-center gap-3 h-auto py-6 rounded-xl hover:bg-blue-50 hover:border-blue-200 transition-all duration-200"
                      >
                        <div className="h-10 w-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                          <Mail className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-sm font-medium">Email</span>
                      </Button>

                      <Button
                        variant="outline"
                        onClick={handleSMSShare}
                        className="flex flex-col items-center gap-3 h-auto py-6 rounded-xl hover:bg-green-50 hover:border-green-200 transition-all duration-200"
                      >
                        <div className="h-10 w-10 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                          <MessageSquare className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-sm font-medium">SMS</span>
                      </Button>

                      <Button
                        variant="outline"
                        onClick={() => handleSocialShare('facebook')}
                        className="flex flex-col items-center gap-3 h-auto py-6 rounded-xl hover:bg-blue-50 hover:border-blue-200 transition-all duration-200"
                      >
                        <div className="h-10 w-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                          <Facebook className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-sm font-medium">Facebook</span>
                      </Button>

                      <Button
                        variant="outline"
                        onClick={() => handleSocialShare('twitter')}
                        className="flex flex-col items-center gap-3 h-auto py-6 rounded-xl hover:bg-sky-50 hover:border-sky-200 transition-all duration-200"
                      >
                        <div className="h-10 w-10 bg-gradient-to-r from-sky-400 to-sky-500 rounded-lg flex items-center justify-center">
                          <Twitter className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-sm font-medium">Twitter</span>
                      </Button>
                    </div>
                  </div>
                </motion.div>

                {/* Usage Statistics Card */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 border-b border-orange-100">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 bg-gradient-to-r from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
                        <TrendingUp className="h-4 w-4 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Usage Statistics</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-3 gap-6 text-center">
                      <div className="space-y-2">
                        <div className="text-3xl font-bold text-jamaica-green">{discount.currentUses}</div>
                        <div className="text-sm text-gray-600 font-medium">Times Used</div>
                      </div>
                      <div className="space-y-2">
                        <div className="text-3xl font-bold text-jamaica-green">{discount.maxUses - discount.currentUses}</div>
                        <div className="text-sm text-gray-600 font-medium">Remaining</div>
                      </div>
                      <div className="space-y-2">
                        <div className="text-3xl font-bold text-jamaica-green">
                          {((discount.currentUses / discount.maxUses) * 100).toFixed(0)}%
                        </div>
                        <div className="text-sm text-gray-600 font-medium">Used</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-8 py-4 bg-gray-50">
              <div className="flex items-center justify-end">
                <Button onClick={handleClose} variant="default">
                  Done
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
} 